import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  LucideCircleUser,
  Truck,
} from "lucide-react";
import axios from "axios";
import { FaCoins, FaRupeeSign } from "react-icons/fa";
import { ColorRing } from "react-loader-spinner";
import convertToCr from "../../ui/convertToCr";

const ClientDashboard = () => {
  const { clientId } = useParams();
  const [clientDetails, setClientDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchClientData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `buildApiUrl("/api/orderItem/order/client/clientordersummary/${clientId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setClientDetails(response.data.clientDetails);
        setLoading(false);
      } catch (err) {
        setError("Failed to load data");
        setLoading(false);
      }
    };

    fetchClientData();
  }, [clientId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <ColorRing
          visible={true}
          height="120"
          width="120"
          ariaLabel="color-ring-loading"
          wrapperClass="color-ring-wrapper"
          colors={["#3b82f6", "#60a5fa", "#93c5fd", "#c4b5fd", "#a5b4fc"]}
        />
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-sm text-red-600">{error}</div>;
  }

  if (!clientDetails) {
    return (
      <div className="text-center text-sm text-gray-600">
        No client data available
      </div>
    );
  }

  return (
    <div className="">
      <div className="max-w-7xl mx-auto space-y-4">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-600 flex items-center gap-2">
          <LucideCircleUser className="text-red-600" size={24} />
          Client Dashboard
        </h1>

        <FinancialSummary clientDetails={clientDetails} />

        <h2 className="text-sm font-medium text-gray-700">Order Details</h2>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Table Header */}
          <table className="w-full text-sm text-gray-600">
            <thead className="bg-gray-100 text-xs font-medium text-gray-600 uppercase">
              <tr>
                <th className="px-4 py-2 text-left">Order ID</th>
                <th className="px-4 py-2 text-left">Order Date</th>
                <th className="px-4 py-2 text-left">Total Amount</th>
                <th className="px-4 py-2 text-left">TPT Charges</th>
                <th className="px-4 py-2 text-left">Delivery Status</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Actions</th>
              </tr>
            </thead>
            {/* Order Cards */}
            <tbody>
              {clientDetails.totalOrderDetails.map((order) => (
                <OrderCard key={order.orderId} order={order} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const FinancialSummary = ({ clientDetails }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
    <FinancialCard
      title="Total Estimate"
      amount={clientDetails.totalEstimateAmount}
      icon={<FaRupeeSign className="h-4 w-4 text-blue-600" />}
    />
    <FinancialCard
      title="Current Balance"
      amount={clientDetails.currentBalance}
      icon={<FaCoins className="h-4 w-4 text-green-600" />}
    />
    <FinancialCard
      title="Paid Balance"
      amount={clientDetails.paidBalance}
      icon={<FaRupeeSign className="h-4 w-4 text-purple-600" />}
    />
    <FinancialCard
      title="Total TPT Charges"
      amount={clientDetails.totalTPTCharges}
      icon={<Truck className="h-4 w-4 text-indigo-600" />}
    />
  </div>
);

const FinancialCard = ({ title, amount, icon }) => {
  const formattedAmount =
    typeof amount === "number" && !isNaN(amount) ? amount.toFixed(2) : "0.00";
  const isNegative = parseFloat(formattedAmount) < 0;

  return (
    <div className="bg-white rounded-md shadow-sm p-3 border border-gray-200 hover:shadow-md transition-shadow duration-200">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-gray-500">{title}</span>
        <span className="p-1.5 bg-gray-100 rounded-full">{icon}</span>
      </div>
      <div className="flex items-baseline gap-1">
        <span
          className={`text-sm font-semibold ${
            isNegative ? "text-green-600" : "text-gray-800"
          }`}
        >
          {convertToCr(Math.abs(parseFloat(formattedAmount)).toFixed(2))}
        </span>
        {isNegative && (
          <span className="text-xs font-medium text-green-500 bg-green-50 px-1.5 py-0.5 rounded-full">
            Adv.
          </span>
        )}
      </div>
    </div>
  );
};

const OrderCard = ({ order }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <tr className="hover:bg-gray-50 border-b border-t border-gray-200 divide-x divide-gray-200 transition-colors">
        <td className="px-4 py-1 uppercase font-medium text-gray-600">
          {order.orderId.slice(-5)}
        </td>
        <td className="px-4 py-1">{order.orderDate}</td>
        <td className="px-4 py-1">
          {order.totalAmount ? order.totalAmount.toLocaleString() : 0}
        </td>
        <td className="px-4 py-1">
          {order.tptCharges ? order.tptCharges.toLocaleString() : 0}
        </td>
        <td className="px-4 py-1">
          <span
            className={`font-medium ${
              order.orderDeliveredStatus === "Dispatched"
                ? "text-green-600"
                : "text-yellow-600"
            }`}
          >
            {order.orderDeliveredStatus}
          </span>
        </td>
        <td className="px-4 py-1">
          <span
            className={`px-2 py-0.5 rounded-full text-[10px]  font-medium ${
              order.status === "Accept"
                ? "bg-green-100 text-green-800"
                : "bg-yellow-100 text-yellow-800"
            }`}
          >
            {order.status}
          </span>
        </td>
        <td className="px-4 py-1">
          <button
            className="flex items-center text-blue-600 hover:text-blue-700 transition-colors"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        </td>
      </tr>
      {isExpanded && <ItemDetails items={order.items} />}
    </>
  );
};

const ItemDetails = ({ items }) => (
  <tr>
    <td colSpan={8} className="p-2">
      <div className=" bg-gray-50 rounded-md border border-gray-300 overflow-x-auto">
        <table className="w-full text-sm text-gray-600">
          <thead className="bg-gray-100 text-xs font-medium text-gray-600">
            <tr>
              <th className="px-4 py-1 text-left">Item Name</th>
              <th className="px-4 py-1 text-left">Category</th>
              <th className="px-4 py-1 text-left">Code</th>
              <th className="px-4 py-1 text-left">Quantity</th>
              <th className="px-4 py-1 text-left">Price</th>
              <th className="px-4 py-1 text-left">Total</th>
              <th className="px-4 py-1 text-left">Warehouse</th>
              <th className="px-4 py-1 text-left">Inward Ref</th>
              <th className="px-4 py-1 text-left">Shipment Mark</th>
              <th className="px-4 py-1 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr
                key={item._id}
                className={`hover:bg-gray-100 transition-colors ${
                  index % 2 === 0 ? "bg-white" : "bg-gray-50"
                }`}
              >
                <td className="px-4 py-0.5 border-t border-gray-200">
                  {item.itemName}
                </td>
                <td className="px-4 py-0.5 border-t border-gray-200">
                  {item.categoryName}
                </td>
                <td className="px-4 py-0.5 border-t border-gray-200">
                  {item.itemCode}
                </td>
                <td className="px-4 py-0.5 border-t border-gray-200">
                  {item.qty}
                </td>
                <td className="px-4 py-0.5 border-t border-gray-200">
                  {item.price ? item.price.toLocaleString() : "N/A"}
                </td>
                <td className="px-4 py-0.5 border-t border-gray-200">
                  {item.totalAmount ? item.totalAmount.toLocaleString() : "N/A"}
                </td>
                <td className="px-4 py-0.5 border-t border-gray-200">
                  {item.wareHouseName}
                </td>
                <td className="px-4 py-0.5 border-t border-gray-200">
                  {item.inwardReference}
                </td>
                <td className="px-4 py-0.5 border-t border-gray-200">
                  {item.inwardShipmentMark || "-"}
                </td>
                <td className="px-4 py-0.5 border-t border-gray-200">
                  <span
                    className={`px-2  inline-flex text-[10px] font-medium rounded-full ${
                      item.deliveryStatus === "Dispatched"
                        ? "bg-green-100 text-green-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {item.deliveryStatus}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </td>
  </tr>
);

export default ClientDashboard;
